import type { AgentReport } from '@ralph/contracts'
import { MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Select } from '@/components/ui/primitives.tsx'
import { useAgentReports, useTriageReport } from '@/hooks/use-engagement.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'

/**
 * Triage (AGM-123).
 *
 * Open reports first and open-only by default: a queue that shows a month of dismissed
 * reports on top of four live ones is a queue nobody works. Everything stays reachable
 * through the filter, because the dismissed ones are the record of what was decided.
 *
 * The per-reporter count next to a name is computed from the queue itself. It is the
 * "false-report patterns are visible to admins" requirement in its smallest honest form — a
 * count of what this person has open right now, with no inference attached to it.
 */
export const ReportsQueue = ({ enabled }: { enabled: boolean }) => {
  const t = useT().admin.reports
  const reports = useAgentReports(enabled)
  const [openOnly, setOpenOnly] = useState(true)

  const items = reports.data?.items ?? []
  const byReporter = new Map<string, number>()
  for (const report of items) byReporter.set(report.reporterName, (byReporter.get(report.reporterName) ?? 0) + 1)

  const visible = [...items]
    .filter((report) => !openOnly || report.status === 'open')
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === 'open' ? -1 : 1
      return Date.parse(b.createdAt) - Date.parse(a.createdAt)
    })

  return (
    <section>
      <SectionHead
        title={t.title}
        lead={t.lead}
        action={
          <>
            <label htmlFor="report-filter" className="eyebrow">
              {t.filterLabel}
            </label>
            {/* The primitive is `w-full` by design, so the width lives on a wrapper rather
                than on a competing utility that Tailwind may or may not order after it. */}
            <div className="w-[9.5rem]">
              <Select
                id="report-filter"
                value={openOnly ? 'open' : 'all'}
                onChange={(event) => setOpenOnly(event.target.value === 'open')}
              >
                <option value="open">{t.filterOpen}</option>
                <option value="all">{t.filterAll}</option>
              </Select>
            </div>
          </>
        }
      />

      {reports.isLoading && <p className="mt-3 text-[13px] text-fg-subtle">{t.loading}</p>}

      {reports.isError && (
        <div className="mt-3">
          <EmptyState title={t.unavailable} body={t.unavailableBody} />
        </div>
      )}

      {!reports.isLoading && !reports.isError && visible.length === 0 && (
        <div className="mt-3">
          <EmptyState title={t.empty} body={t.emptyBody} />
        </div>
      )}

      <ul className="mt-4 space-y-2.5">
        {visible.map((report, index) => (
          <li key={report.id}>
            <ReportRow report={report} index={index} reporterTotal={byReporter.get(report.reporterName) ?? 1} />
          </li>
        ))}
      </ul>
    </section>
  )
}

const ReportRow = ({ report, reporterTotal, index }: { report: AgentReport; reporterTotal: number; index: number }) => {
  // Bound in the component body; a module-scope capture would freeze the locale (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.admin.reports
  const triage = useTriageReport()

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5">
        <div className="min-w-0 flex-1 basis-[15rem]">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <Badge tone={report.category === 'harmful' || report.category === 'impersonation' ? 'alert' : 'neutral'}>
              {t.categories[report.category]}
            </Badge>
            <Link
              to={`/agents/${report.agentId}`}
              className="min-w-0 truncate text-[14px] font-semibold hover:underline"
            >
              {report.agentDisplayName}
            </Link>
            <span className="readout text-fg-subtle">{report.agentId}</span>
            <Badge tone={report.status === 'open' ? 'primary' : 'neutral'}>{t.statuses[report.status]}</Badge>
          </div>

          {/* The reporter's own words, quoted rather than paraphrased: the whole point of the
              queue is that a human wrote this and another human decides what it means. */}
          <p className="mt-2 text-[13px] leading-relaxed text-pretty">
            {report.note.trim() === '' ? <span className="text-fg-subtle">{t.noteEmpty}</span> : report.note}
          </p>

          <p className="readout mt-1.5 text-fg-subtle">
            {t.reportedBy(report.reporterName)} · {fmt.formatAgo(report.createdAt)}
            {reporterTotal > 1 && ` · ${t.repeatReporter(fmt, reporterTotal)}`}
          </p>

          {report.conversationId ? (
            <Link
              to={`/c/${report.conversationId}`}
              className="readout mt-1.5 inline-flex items-center gap-1.5 text-fg-subtle hover:text-fg"
            >
              <MessageSquare size={11} aria-hidden />
              {t.openConversation}
            </Link>
          ) : (
            <p className="readout mt-1.5 text-fg-subtle">{t.noConversation}</p>
          )}
        </div>

        {report.status === 'open' && (
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              size="sm"
              disabled={triage.isPending}
              onClick={() => triage.mutate({ reportId: report.id, status: 'triaged' })}
            >
              {triage.isPending ? t.working : t.triage}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={triage.isPending}
              onClick={() => triage.mutate({ reportId: report.id, status: 'dismissed' })}
            >
              {t.dismiss}
            </Button>
          </div>
        )}
      </div>

      {triage.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-alert">
          {t.failed} {describeApiError(fmt.t, triage.error)}
        </p>
      )}
    </StudioRow>
  )
}
